///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2022
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import PendingBidsLoading from "../components/pending-bids/PendingBidsLoading";
import NoPendingBids from "../components/pending-bids/NoPendingBids";
import Close from "../components/icons/Close";
import Check from "../components/icons/Check";
import Avatar from "../components/Avatar";
import {
  fetchPendingBids,
  filterPendingBids,
  acceptPendingBid,
  rejectPendingBidSuccess,
  resetAcceptPendingBidState
} from "../slices/pendingBidsSlice";
import PendingBidsTableHeader from "../components/pending-bids/PendingBidsTableHeader";
import SearchBox from "../components/SearchBox";
import SystemError from "../components/SystemError";
import RejectBidModal from "../components/RejectBidModal";
import Tooltip from "../components/Tooltip";
import Spinner from "../components/icons/Spinner";
import SystemErrorModal from "../components/SystemErrorModal";
import messages from "../helpers/messages";
import redirectToBid from "../helpers/redirectToBid";
import { userRole, userRoleListForPendingBids } from "../helpers/types";
import AutoComplete from "../components/form-controls/AutoComplete";
import { setUserRoleForManagementUser } from "../slices/userSlice";

function PendingBids() {
  const [rejectDetails, setRejectDetails] = useState({
    open: false,
    bidId: null,
    modeId: null
  });
  const dispatch = useDispatch();
  const history = useHistory();

  const pendingBids = useSelector(state => state.pendingBids);
  const { bids, isFetching, searchTerm, isError } = pendingBids;
  const { user } = useSelector(state => state.user);

  useEffect(() => {
    if (
      user.role === userRole.management &&
      !userRoleListForPendingBids.find(
        x => x.value === user.userRoleForManagementUser
      )
    ) {
      dispatch(setUserRoleForManagementUser(userRole.tlPricingAnalyst));
    }
  }, [dispatch, user]);

  useEffect(() => {
    dispatch(fetchPendingBids());
  }, [dispatch]);

  const reloadPage = () => {
    dispatch(fetchPendingBids());
  };

  const handleAcceptPendingBid = (bidId, bidModeId) => event => {
    event.stopPropagation();
    dispatch(acceptPendingBid(bidId, bidModeId));
  };

  const handleRejectPendingBid = (bidId, bidModeId) => event => {
    event.stopPropagation();
    setRejectDetails({ open: true, bidId, bidModeId });
  };

  const BodyCell = ({ className, value, children, ...rest }) => (
    <td className={"body-cell " + className} {...rest}>
      {value || children}
    </td>
  );

  const handleRejectDetailsClose = success => {
    success && dispatch(rejectPendingBidSuccess(rejectDetails.bidModeId));
    setRejectDetails({ open: false, bidId: null, modeId: null });
  };

  return (
    <>
      <RejectBidModal
        {...rejectDetails}
        handleClose={handleRejectDetailsClose}
      />
      {isError ? (
        <SystemError reloadPage={reloadPage} />
      ) : (
        <div
          data-testid="pending-bids"
          className="pending-bids p-24 full-height"
        >
          {isFetching || (bids && bids.length) ? (
            <div className="pending-bids-container">
              <div className="one-whole">
                <div className="grid__cell one-half">
                  <h1 id="pending-bids-title">
                    {messages.pendingBids.defaultMessage}
                  </h1>
                </div>
                <div className="grid__cell one-half text-right">
                  {user.role === userRole.management && (
                    <div
                      className="inline-block mr-12"
                      data-testid="userRoleToView"
                    >
                      <AutoComplete
                        label={messages.userRoleToView.defaultMessage}
                        staticOptions={userRoleListForPendingBids}
                        value={userRoleListForPendingBids.find(
                          x => x.value === user.userRoleForManagementUser
                        )}
                        handleChange={userRole => {
                          dispatch(
                            setUserRoleForManagementUser(userRole.value)
                          );
                          dispatch(fetchPendingBids());
                        }}
                      />
                    </div>
                  )}
                  <SearchBox
                    value={searchTerm}
                    onChange={event =>
                      event.target &&
                      dispatch(filterPendingBids(event.target.value))
                    }
                  />
                </div>
              </div>
              {isFetching ? (
                <PendingBidsLoading />
              ) : (
                <>
                  <table className="one-whole pending-bids-grid">
                    <PendingBidsTableHeader />
                    <tbody>
                      {bids.map((bid, index) => {
                        const nameInitials = bid.mainRep.firstName[0].concat(
                          bid.mainRep.lastName[0]
                        );

                        return (
                          bid.filtered && (
                            <tr
                              className="row"
                              key={index}
                              onClick={e =>
                                redirectToBid(e, bid.bidId, history)
                              }
                            >
                              <BodyCell value={bid.bidName} />
                              <BodyCell>
                                <Avatar
                                  className="inline-block mb-0 mr-8"
                                  imageSource={bid.mainRep.profilePicture}
                                  size={24}
                                  data-testid="avatar"
                                  alt={bid.mainRep.profilePicture}
                                  textContent={nameInitials}
                                  fontSize={10}
                                />
                                <label
                                  style={{ maxWidth: 100 }}
                                  className="inline-block align-top pt-6 truncate"
                                >
                                  {bid.mainRep.fullName}
                                </label>
                              </BodyCell>
                              {bid.shipperOpsGroupOffice ? (
                                <Tooltip
                                  content={bid.shipperOpsGroupOffice}
                                  offset={[12, 0]}
                                >
                                  <td className="body-cell">
                                    {bid.shipperName}
                                  </td>
                                </Tooltip>
                              ) : (
                                <td className="body-cell">{bid.shipperName}</td>
                              )}
                              <BodyCell value={bid.formattedDeadline} />
                              <BodyCell
                                className="text-right"
                                value={bid.laneCount}
                              />
                              <BodyCell
                                className="text-right"
                                value={bid.volume}
                              />
                              <BodyCell value={bid.mode} />
                              <BodyCell
                                className="text-right"
                                value={
                                  <FormattedMessage
                                    {...messages.timeSinceLastUpdateDuration}
                                    values={{
                                      timeSinceLastUpdateDuration:
                                        bid.formattedPendingDuration
                                    }}
                                  />
                                }
                              />
                              <BodyCell>
                                {bid.isAcceptFetching ? (
                                  <Spinner
                                    className="spinner pos-relative ml-4 mr-12"
                                    height="12px"
                                    width="12px"
                                    fill="#0088D1"
                                  />
                                ) : (
                                  <Check
                                    width="24px"
                                    height="24px"
                                    data-testid="accept-bid"
                                    className="pointer check-icon round mr-4"
                                    title=""
                                    onClick={handleAcceptPendingBid(
                                      bid.bidId,
                                      bid.bidModeId
                                    )}
                                  />
                                )}
                                <Close
                                  width="24px"
                                  height="24px"
                                  data-testid="reject-bid"
                                  className="pointer close-icon round"
                                  title=""
                                  onClick={handleRejectPendingBid(
                                    bid.bidId,
                                    bid.bidModeId
                                  )}
                                />
                              </BodyCell>
                            </tr>
                          )
                        );
                      })}
                    </tbody>
                  </table>
                  {bids.map((bid, index) => {
                    return (
                      bid.isAcceptFailure && (
                        <SystemErrorModal
                          key={index}
                          open={bid.isAcceptFailure}
                          large={true}
                          closeModal={() =>
                            dispatch(resetAcceptPendingBidState(bid.bidModeId))
                          }
                          handleExit={() =>
                            dispatch(resetAcceptPendingBidState(bid.bidModeId))
                          }
                          handleTryAgain={handleAcceptPendingBid(
                            bid.bidId,
                            bid.bidModeId
                          )}
                        />
                      )
                    );
                  })}
                </>
              )}
            </div>
          ) : (
            <NoPendingBids />
          )}
        </div>
      )}
    </>
  );
}

export default PendingBids;
